/**
 * MyRequestCard Component
 * Feature 014: Service Provider Engagements - Care Seeker View
 *
 * Card component showing consultation request status for care seekers
 * Displays provider info, request details, and current status
 */

import { Card } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { MessageCircle, Clock, Calendar, CheckCircle2, XCircle, FileText } from 'lucide-react';
import type { ConsultationRequest } from '../types';
import { formatDistanceToNow } from 'date-fns';

type MyRequestCardProps = {
  request: ConsultationRequest;
  onViewConversation?: (providerUserId: string) => void;
  onViewProvider?: (providerId: string) => void;
};

export function MyRequestCard({
  request,
  onViewConversation,
  onViewProvider,
}: MyRequestCardProps) {
  const timeAgo = formatDistanceToNow(new Date(request.createdAt), {
    addSuffix: true,
  });

  const getStatusBadge = () => {
    switch (request.status) {
      case 'PENDING':
        return (
          <Badge className="bg-yellow-500 hover:bg-yellow-500">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'ACCEPTED':
        return (
          <Badge className="bg-green-600 hover:bg-green-600">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Accepted
          </Badge>
        );
      case 'DECLINED':
        return (
          <Badge className="bg-red-600 hover:bg-red-600">
            <XCircle className="w-3 h-3 mr-1" />
            Declined
          </Badge>
        );
      default:
        return null;
    }
  };

  const getStatusMessage = () => {
    switch (request.status) {
      case 'PENDING':
        return 'Waiting for provider response';
      case 'ACCEPTED':
        return 'Provider has accepted your request. You can now message them to schedule.';
      case 'DECLINED':
        return request.responseMessage || 'Provider declined this request';
      default:
        return '';
    }
  };

  return (
    <Card className="p-6">
      <div className="flex gap-4">
        {/* Provider Info */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {request.provider?.companyName || 'Service Provider'}
              </h3>
              <p className="text-sm text-gray-500">{timeAgo}</p>
            </div>
            {getStatusBadge()}
          </div>

          {/* Status Message */}
          <div className="mb-4">
            <p
              className={`text-sm ${
                request.status === 'DECLINED' ? 'text-red-600' : 'text-gray-700'
              }`}
            >
              {getStatusMessage()}
            </p>
          </div>

          {/* Request Details */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-2 mb-2">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Your Request:</p>
                <p className="text-sm text-gray-600">{request.description}</p>
              </div>
            </div>

            {/* Availability */}
            {request.availabilityTimes.length > 0 && (
              <div className="mt-3 space-y-2">
                {/* Times */}
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <div className="flex flex-wrap gap-2">
                    {request.availabilityTimes.map((time) => (
                      <Badge key={time} variant="outline" className="text-xs">
                        {time.charAt(0).toUpperCase() + time.slice(1)}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Days */}
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div className="flex flex-wrap gap-2">
                    {request.availabilityDays.map((day) => (
                      <Badge key={day} variant="outline" className="text-xs">
                        {day.charAt(0).toUpperCase() + day.slice(1).substring(0, 3)}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {request.status === 'ACCEPTED' && onViewConversation && (
              <Button
                size="sm"
                onClick={() => onViewConversation(request.providerUserId)}
                className="bg-[#026857] hover:bg-[#025247]"
              >
                <MessageCircle className="w-4 h-4 mr-1" />
                Message Provider
              </Button>
            )}

            {onViewProvider && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onViewProvider(request.providerId)}
              >
                View Provider Profile
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
